import { useEffect, useState } from 'react';

interface UseObserveOptions {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
}

// Observes elements by `ids`; returns the set currently intersecting the root.
export default function useObserve(
  ids: string[],
  { root = null, rootMargin, threshold = 0 }: UseObserveOptions = {},
) {
  const [visibleIds, setVisibleIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      entries => {
        setVisibleIds(prev => {
          const next = new Set(prev);
          for (const entry of entries) {
            if (entry.isIntersecting) next.add(entry.target.id);
            else next.delete(entry.target.id);
          }
          return next;
        });
      },
      { root, rootMargin, threshold },
    );

    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [ids, root, rootMargin, threshold]);

  return visibleIds;
}
